import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  CheckSquare,
  FolderOpen,
  Pin,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { useParams, Link } from "react-router-dom";
import { useRoomMembers, useRooms } from "@/lib/rooms";
import { useFiles, downloadFile } from "@/lib/files";
import { useTaskSummary, usePinnedMessages } from "@/lib/activity";
import { Avatar } from "@/components/ui/Avatar";
import { Badge } from "@/components/ui/Badge";
import { toggleContext, type ContextTab } from "@/lib/uiState";
import { formatBytes, formatRelative } from "@/lib/format";
import { useMembers } from "@/lib/workspace";
import { useSession } from "@/lib/auth";
import { apiPost, apiDelete } from "@/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

type Tab = ContextTab;

interface ContextPaneProps {
  initialTab?: Tab;
  tab?: Tab;
  onTabChange?: (tab: Tab) => void;
}

interface TabDef {
  id: Tab;
  labelKey: string;
  icon: LucideIcon;
}

/* -------------------------------------------------------------------------- */
/* ContextPane                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Right-side detail pane. Tabbed view of room metadata (members, files,
 * pinned messages, activity feed). Built with a roving-tabindex tab strip and
 * proper `tabpanel` semantics so VoiceOver / NVDA / JAWS read it correctly.
 */
export function ContextPane({ initialTab = "members", tab: selectedTab, onTabChange }: ContextPaneProps): JSX.Element {
  const { t } = useTranslation();
  const [localTab, setLocalTab] = useState<Tab>(initialTab);
  const tab = selectedTab ?? localTab;
  const setTab = (next: Tab) => {
    setLocalTab(next);
    onTabChange?.(next);
  };

  const TABS: TabDef[] = [
    { id: "members", labelKey: "common.members", icon: Users },
    { id: "files", labelKey: "nav.files", icon: FolderOpen },
    { id: "pinned", labelKey: "context.pinned", icon: Pin },
    { id: "activity", labelKey: "context.activity", icon: Activity },
  ];

  return (
    <aside
      role="complementary"
      aria-label={t("common.details")}
      className={cn(
        "w-full md:w-80 h-full shrink-0 flex flex-col",
        "bg-surface-base border-l border-border-subtle",
      )}
    >
      {/* ---------- Header ---------- */}
      <div className="h-14 shrink-0 px-3 flex items-center gap-2 border-b border-border-subtle">
        <h2 className="sr-only">{t("common.details")}</h2>
        <TabStrip
          tabs={TABS}
          active={tab}
          onChange={setTab}
          translate={key => t(key, { defaultValue: ({ "context.pinned": "ピン留め", "context.activity": "アクティビティ" } as Record<string, string>)[key] || key })}
        />
        <button
          type="button"
          aria-label={t("common.close")}
          title={t("common.close")}
          onClick={() => toggleContext(false)}
          className={cn(
            "p-1.5 rounded-md text-fg-muted shrink-0",
            "hover:bg-surface-raised hover:text-fg-primary",
            "transition-colors duration-150 ease-out",
            "focus-visible:shadow-focus",
          )}
        >
          <X size={16} aria-hidden />
        </button>
      </div>

      {/* ---------- Panel ---------- */}
      <div
        role="tabpanel"
        id={`context-panel-${tab}`}
        aria-labelledby={`context-tab-${tab}`}
        tabIndex={0}
        className="flex-1 overflow-y-auto p-3 text-sm focus:outline-none"
      >
        {tab === "members" && <MembersList />}
        {tab === "files" && <FilesPanel />}
        {tab === "pinned" && <PinnedPanel />}
        {tab === "activity" && <ActivityPanel />}
      </div>
    </aside>
  );
}

/* -------------------------------------------------------------------------- */
/* TabStrip — accessible tablist with arrow-key roving tabindex               */
/* -------------------------------------------------------------------------- */

function TabStrip({
  tabs,
  active,
  onChange,
  translate,
}: {
  tabs: TabDef[];
  active: Tab;
  onChange: (tab: Tab) => void;
  translate: (k: string) => string;
}): JSX.Element {
  const refs = useRef<Record<Tab, HTMLButtonElement | null>>({
    members: null,
    files: null,
    pinned: null,
    activity: null,
  });

  /* Keep focus on the currently-active tab when it changes via keyboard. */
  useEffect(() => {
    if (document.activeElement && tabs.some((t) => refs.current[t.id] === document.activeElement)) {
      refs.current[active]?.focus();
    }
  }, [active, tabs]);

  const onKey = (e: React.KeyboardEvent) => {
    const i = tabs.findIndex((tab) => tab.id === active);
    if (i < 0) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      onChange(tabs[(i + 1) % tabs.length]!.id);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      onChange(tabs[(i - 1 + tabs.length) % tabs.length]!.id);
    } else if (e.key === "Home") {
      e.preventDefault();
      onChange(tabs[0]!.id);
    } else if (e.key === "End") {
      e.preventDefault();
      onChange(tabs[tabs.length - 1]!.id);
    }
  };

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      onKeyDown={onKey}
      className="flex-1 min-w-0 flex items-center gap-0.5 overflow-x-auto"
    >
      {tabs.map((tab) => {
        const isActive = tab.id === active;
        const Icon = tab.icon;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              refs.current[tab.id] = el;
            }}
            id={`context-tab-${tab.id}`}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls={`context-panel-${tab.id}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(tab.id)}
            className={cn(
              "px-2.5 h-8 inline-flex items-center gap-1.5 rounded-md text-sm shrink-0",
              "transition-colors duration-150 ease-out",
              "focus-visible:shadow-focus",
              isActive
                ? "bg-brand-100 text-brand-700 font-medium"
                : "text-fg-secondary hover:bg-surface-raised hover:text-fg-primary",
            )}
          >
            <Icon size={14} aria-hidden />
            <span>{translate(tab.labelKey)}</span>
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Members tab                                                                */
/* -------------------------------------------------------------------------- */

function MembersList(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const session = useSession();
  const queryClient = useQueryClient();
  const { data: members = [], error, isPending } = useRoomMembers(id);
  const { rooms } = useRooms();
  const organization = useMembers();
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const canManage = rooms.find(room => room.id === id)?.kind !== "dm" && ["owner", "admin"].includes(members.find(member => member.id === session?.user.id)?.role || "");
  useEffect(() => { setTarget(""); setActionError(null); }, [id]);
  const update = async (operation: () => Promise<unknown>) => {
    setBusy(true); setActionError(null);
    try { await operation(); await queryClient.invalidateQueries({ queryKey: ["room-members", id] }); setTarget(""); }
    catch (e) { setActionError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  if (!id) return <p className="text-fg-muted">ルームを選ぶと参加メンバーを表示します。</p>;
  if (error) return <p role="alert">{error.message}</p>;
  return (
    <section aria-labelledby="ctx-members-heading">
      <SectionHeader id="ctx-members-heading">
        {t("common.members")}
        <span className="ml-1 text-fg-muted font-normal">({members.length})</span>
      </SectionHeader>
      {isPending && <p role="status">メンバーを読み込んでいます…</p>}
      {actionError && <p role="alert" className="text-danger text-sm mb-3">{actionError}</p>}
      {canManage && <form className="space-y-2 mb-4" onSubmit={e => { e.preventDefault(); void update(() => apiPost(`/v1/rooms/${encodeURIComponent(id!)}/members`, { user_ids: [target] })); }}>
        <label className="block text-xs space-y-1">メンバーを追加<select className="block border rounded p-2 w-full text-sm" value={target} disabled={busy} onChange={e => setTarget(e.target.value)}><option value="">組織のメンバーを選択</option>{organization.members.filter(member => !members.some(current => current.id === member.id)).map(member => <option key={member.id} value={member.id}>{member.displayName}</option>)}</select></label>
        <Button size="sm" type="submit" disabled={busy || !target}>ルームに追加</Button>
        {organization.error && <p role="alert" className="text-danger text-sm">{organization.error.message}</p>}
      </form>}
      <ul className="space-y-0.5">
        {members.map((u) => (
          <li key={u.id} className="flex items-center gap-1">
            <Link
              to={u.id === session?.user.id ? "/settings/profile" : `/dm?user=${encodeURIComponent(u.id)}`}
              className={cn(
                "w-full flex items-center gap-2.5 p-2 rounded-md text-left",
                "hover:bg-surface-raised transition-colors duration-150 ease-out",
                "focus-visible:shadow-focus",
              )}
            >
              <Avatar name={u.displayName} size={28} presence={u.presence} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{u.displayName}</p>
                <p className="text-[11px] text-fg-muted truncate">{u.email}</p>
              </div>
              {u.presence === "online" ? (
                <span className="text-[10px] text-accent-call font-medium">●</span>
              ) : null}
            </Link>
            {canManage && u.id !== session?.user.id && u.role !== "owner" && <button disabled={busy} className="p-1 text-fg-muted hover:text-danger rounded" aria-label={`${u.displayName} をルームから削除`} onClick={() => { if (window.confirm(`${u.displayName} をこのルームから削除しますか？`)) void update(() => apiDelete(`/v1/rooms/${encodeURIComponent(id!)}/members/${encodeURIComponent(u.id)}`)); }}><X size={14} aria-hidden /></button>}
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Files tab                                                                  */
/* -------------------------------------------------------------------------- */

function FilesPanel(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const result = useFiles();
  const files = result.files.filter(file => !id || file.roomId === id);
  const [error, setError] = useState<string | null>(null);
  if (error || result.error) return <p role="alert">{error || result.error?.message}</p>;
  if (files.length === 0) {
    return (
      <EmptyState
        icon={<FolderOpen size={36} aria-hidden className="text-fg-subtle" />}
        title={t("files.empty")}
        hint={t("files.drop")}
      />
    );
  }
  return (
    <section aria-labelledby="ctx-files-heading" className="space-y-2">
      <SectionHeader id="ctx-files-heading">
        {t("nav.files")}
        <span className="ml-1 text-fg-muted font-normal">({files.length})</span>
      </SectionHeader>
      <ul className="space-y-1.5">
        {files.map((f) => (
          <li key={f.id}>
            <button
              type="button"
              onClick={() => void downloadFile(f).catch(e => setError(String(e)))}
              className={cn(
                "w-full p-2.5 rounded-md text-left border border-border-subtle bg-canvas",
                "hover:bg-surface-raised hover:border-border-default",
                "transition-colors duration-150 ease-out",
                "focus-visible:shadow-focus",
              )}
            >
              <p className="text-sm font-medium truncate" title={f.name}>
                {f.name}
              </p>
              <div className="text-[11px] text-fg-muted flex items-center gap-1.5 mt-1">
                <span>{formatBytes(f.size)}</span>
                <span aria-hidden>·</span>
                <span className="truncate">
                  {f.uploaderId}
                </span>
                <span aria-hidden>·</span>
                <time dateTime={f.uploadedAt} className="shrink-0">
                  {formatRelative(f.uploadedAt)}
                </time>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Pinned tab                                                                 */
/* -------------------------------------------------------------------------- */

function PinnedPanel(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const pins = usePinnedMessages(id);
  if (pins.error) return <p role="alert">{pins.error.message}</p>;
  if (pins.data?.length) return <ul className="space-y-3">{pins.data.map(pin => <li key={pin.id}><Link to={`/rooms/${encodeURIComponent(pin.roomId)}?message=${encodeURIComponent(pin.id)}`} className="block rounded border p-3">{pin.preview}</Link></li>)}</ul>;
  return (
    <EmptyState
      icon={<Pin size={36} aria-hidden className="text-fg-subtle" />}
      title={t("context.pinnedEmpty.title", {
        defaultValue: "ピン留めされたメッセージはありません",
      })}
      hint={t("context.pinnedEmpty.hint", {
        defaultValue: "重要なメッセージにカーソルを合わせ、メニューから「ピン留め」を選びます。",
      })}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Activity tab                                                               */
/* -------------------------------------------------------------------------- */

function ActivityPanel(): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams();
  const tasks = useTaskSummary(id);
  const recentTasks = (tasks.data || []).slice(0, 5);
  if (tasks.error) return <p role="alert">{tasks.error.message}</p>;

  return (
    <section aria-labelledby="ctx-activity-heading" className="space-y-3">
      <SectionHeader id="ctx-activity-heading">
        {t("context.activity", { defaultValue: "アクティビティ" })}
      </SectionHeader>
      <ul className="space-y-2">
        {recentTasks.map((task) => (
          <li
            key={task.id}
            className={cn(
              "p-2.5 rounded-md border border-border-subtle bg-canvas",
              "hover:bg-surface-raised hover:border-border-default",
              "transition-colors duration-150 ease-out",
            )}
          >
            <div className="flex items-start gap-2">
              <CheckSquare
                size={14}
                aria-hidden
                className={cn(
                  "shrink-0 mt-0.5",
                  task.status === "done" ? "text-accent-call" : "text-accent-task",
                )}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{task.title}</p>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <Badge
                    tone={
                      task.status === "done"
                        ? "success"
                        : task.status === "review"
                          ? "info"
                          : task.status === "in_progress"
                            ? "warning"
                            : "neutral"
                    }
                    className="text-[10px] px-1.5 py-0"
                  >
                    {t(`tasks.status.${camel(task.status)}`)}
                  </Badge>
                  {task.priority === "urgent" ? (
                    <Badge tone="danger" className="text-[10px] px-1.5 py-0">
                      {t("tasks.priority.urgent")}
                    </Badge>
                  ) : null}
                  {task.dueAt ? (
                    <time dateTime={task.dueAt} className="text-[11px] text-fg-muted">
                      {formatRelative(task.dueAt)}
                    </time>
                  ) : null}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable bits                                                              */
/* -------------------------------------------------------------------------- */

function SectionHeader({
  id,
  children,
}: {
  id?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <h3
      id={id}
      className="px-1 mb-2 text-[11px] font-semibold uppercase tracking-wide text-fg-muted"
    >
      {children}
    </h3>
  );
}

function EmptyState({
  icon,
  title,
  hint,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
}): JSX.Element {
  return (
    <div className="h-full min-h-[180px] flex flex-col items-center justify-center text-center px-6 py-10">
      <div
        aria-hidden
        className={cn(
          "w-14 h-14 mb-3 rounded-full bg-surface-raised border border-border-subtle",
          "flex items-center justify-center",
        )}
      >
        {icon}
      </div>
      <p className="text-sm font-medium text-fg-secondary">{title}</p>
      {hint ? <p className="mt-1 text-xs text-fg-muted leading-5">{hint}</p> : null}
    </div>
  );
}

function camel(s: string): string {
  return s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
}
